/**
 * Hand-authored battlefield layout — the single source of truth for where the
 * river runs, where the roads go and where the capturable bases sit.
 *
 * Owned by the world module; imported by terrain.js (to carve roads/river/pads
 * into the heightfield) and by bases.js (to place the architecture).
 *
 * Coordinates are metres, Y is up, world spans +/-260.
 */

import { FACTIONS as THEME_FACTIONS } from '../core/theme.js';
import { resolveArena } from './arenas.js';

/** Reusable polyline with arc-length parameterisation + nearest-point query. */
export class Polyline {
  constructor(pts) {
    this.pts = pts;
    this.n = pts.length - 1;
    this.cum = new Float32Array(pts.length);
    for (let i = 0; i < this.n; i++) {
      const dx = pts[i + 1][0] - pts[i][0], dz = pts[i + 1][1] - pts[i][1];
      this.cum[i + 1] = this.cum[i] + Math.hypot(dx, dz);
    }
    this.len = this.cum[this.n];
  }
  /** Nearest point: writes {d, s, cx, cz, tx, tz} into `out`, returns it. */
  query(px, pz, out) {
    let bd = 1e18, bs = 0, bcx = 0, bcz = 0, btx = 1, btz = 0;
    const p = this.pts;
    for (let i = 0; i < this.n; i++) {
      const ax = p[i][0], az = p[i][1];
      const dx = p[i + 1][0] - ax, dz = p[i + 1][1] - az;
      const L2 = dx * dx + dz * dz;
      let t = L2 > 1e-9 ? ((px - ax) * dx + (pz - az) * dz) / L2 : 0;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      const cx = ax + dx * t, cz = az + dz * t;
      const ddx = px - cx, ddz = pz - cz;
      const d2 = ddx * ddx + ddz * ddz;
      if (d2 < bd) {
        bd = d2; const L = Math.sqrt(L2);
        bs = this.cum[i] + t * L;
        bcx = cx; bcz = cz; btx = L > 1e-6 ? dx / L : 1; btz = L > 1e-6 ? dz / L : 0;
      }
    }
    out.d = Math.sqrt(bd); out.s = bs; out.cx = bcx; out.cz = bcz; out.tx = btx; out.tz = btz;
    return out;
  }
  /** Position at arc length s (clamped). */
  at(s, out) {
    s = s < 0 ? 0 : s > this.len ? this.len : s;
    let i = 0;
    while (i < this.n - 1 && this.cum[i + 1] < s) i++;
    const seg = this.cum[i + 1] - this.cum[i];
    const t = seg > 1e-6 ? (s - this.cum[i]) / seg : 0;
    const p = this.pts;
    out.x = p[i][0] + (p[i + 1][0] - p[i][0]) * t;
    out.z = p[i][1] + (p[i + 1][1] - p[i][1]) * t;
    const dx = p[i + 1][0] - p[i][0], dz = p[i + 1][1] - p[i][1];
    const L = Math.hypot(dx, dz) || 1;
    out.tx = dx / L; out.tz = dz / L;
    return out;
  }
}

/* ------------------------------------------------------------------ arena
 * The battlefield is DATA now (see arenas.js). It is resolved HERE, during this
 * module's own evaluation and before RIVER and ROADS are derived below, so that
 * no importer can ever observe a stale or half-swapped arena. Every export name
 * is unchanged, which is why the eleven modules that import from this file did
 * not have to be touched. */
const ARENA = resolveArena(typeof location !== 'undefined' ? location.search : '');

/** Which battlefield this build of the world is carving. */
export const ARENA_ID = ARENA.id;
export const ARENA_NAME = ARENA.name;
/** The sortie a battle on this ground is recorded as when nothing chose one. */
export const DEFAULT_STAGE = ARENA.stage || ARENA.id;
/** The mission script's lines for this ground (see arenas.js `story`). */
export const STORY = ARENA.story || {
  landing: 'The Kidul host is on the field', hold: 'hold the field',
  holdLog: 'form up and hold the field', won: 'The field is ours',
};

/** The named geography terrain.js carves: valley, ridge, mesa, knolls, fields. */
export const LANDFORM = ARENA.landform || {};

/** The river centreline. May be empty for an arena with no river. */
export const RIVER_PTS = ARENA.river;

/** Bridge span, or null for an arena with nothing to bridge. */
export const BRIDGE = ARENA.bridge;

/**
 * Capturable sites. `pad` = radius of flattened ground, `radius` = capture radius.
 * team: 0 = Wilwatikta (player, merah), 1 = Segara Kidul (enemy, indigo),
 * 2 = neutral.
 */
export const SITES = ARENA.sites;

/** Road network. Each entry: half-width of the carriageway + shoulder feather. */
export const ROAD_DEFS = ARENA.roads;

/**
 * Does this arena have a river, and something that crosses it? Lembah Brantas
 * is organised around both; a fortress interior has neither. Every consumer
 * that would otherwise dereference BRIDGE or measure along RIVER reads these,
 * because `null.ax` at module load is a crash on boot and an empty Polyline's
 * `len` is undefined — which feeds NaN into arithmetic silently.
 */
export const HAS_RIVER = Array.isArray(RIVER_PTS) && RIVER_PTS.length >= 2;
export const HAS_BRIDGE = !!BRIDGE;

/** Which staged set-pieces this arena carries, and where the primary ones stand. */
export const TABLEAUX = ARENA.tableaux || { kinds: ['camp', 'battle', 'field'], anchors: {}, expect: {} };

/** Walls that obstruct: collision, sight and gate routing in `walls.js`,
 *  architecture in `bases.js`. An arena without them is open ground. */
export const WALL_DEFS = ARENA.walls || { lines: [] };

/** The authored opening battle: front-line pairs, road columns, officers
 *  and extra streamer hotspots. Lembah Brantas's are the literals the
 *  director always used, and it names no extra hotspots. */
export const OPENING = ARENA.opening || { front: [], cols: [], officers: [], hotspots: [] };

/** What may grow inside an arena's walls, and any authored trees. */
export const FLORA = ARENA.flora || {};

/**
 * Where the props pass may seed roadside camps and old battle lines. The
 * arena's own list if it has one; otherwise every point of its own roads, so
 * dressing always follows THIS arena's roads and never another's.
 */
export const ROADSIDE = Array.isArray(ARENA.roadside) && ARENA.roadside.length
  ? ARENA.roadside
  : ROAD_DEFS.flatMap((r) => r.pts);

export const RIVER = new Polyline(RIVER_PTS);
export const ROADS = ROAD_DEFS.map(r => ({ ...r, line: new Polyline(r.pts) }));

/**
 * Faction palette — used by bases, banners and the minimap.
 *
 * Derived, not declared: the values live in `core/theme.js` so a repaint has
 * one place to happen. Shape is unchanged (`{ key, cloth, trim, accent, dark }`,
 * indexed by team) because `bases.js` and `minimap.js` index it directly.
 */
export const FACTION = THEME_FACTIONS.map(f => ({ key: f.key, ...f.banner }));

const _q = { d: 0, s: 0, cx: 0, cz: 0, tx: 0, tz: 0 };

/** Distance to the nearest site centre and that site (cheap, 7 tests). */
export function nearestSite(x, z) {
  let best = null, bd = 1e18;
  for (const s of SITES) {
    const d = Math.hypot(x - s.x, z - s.z);
    if (d < bd) { bd = d; best = s; }
  }
  return { site: best, d: bd };
}

/** 0..1 "inside a base footprint" weight (used to keep props out of forts). */
export function siteMask(x, z) {
  let m = 0;
  for (const s of SITES) {
    const r = s.pad > 0 ? s.pad : s.radius;
    const d = Math.hypot(x - s.x, z - s.z);
    const w = 1 - Math.min(1, Math.max(0, (d - r * 0.72) / (r * 0.55)));
    if (w > m) m = w;
  }
  return m;
}

/** Distance to the nearest road centreline (metres) and its half-width. */
export function roadDist(x, z) {
  let bd = 1e18, hw = 3, fe = 5;
  for (const r of ROADS) {
    r.line.query(x, z, _q);
    if (_q.d - r.hw < bd - hw) { bd = _q.d; hw = r.hw; fe = r.feather; }
  }
  return { d: bd, hw, feather: fe };
}
